import type { Node } from "@xyflow/react";
import type { OrganizedNode } from "../types/board";
import type { CanvasGraph } from "./canvas-commands";

export const groupId = (node: Node): string | undefined => (node as OrganizedNode).group_id ?? undefined;
export const hasParentTopology = (node: Node): boolean => [node.parentId, (node as Node & { parentNode?: string }).parentNode]
  .some(value => value != null && value !== "") || node.extent === "parent";
export const hasGroups = (graph: CanvasGraph): boolean => graph.nodes.some(node => groupId(node) !== undefined);

export function effectiveStack(node: Node): number {
  const value = node.style?.zIndex;
  // Numeric CSS strings are accepted; CSS keywords and empty strings are not ranks.
  const rank = typeof value === "number" ? value : typeof value === "string" && /^[\t\n\f\r ]*[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?[\t\n\f\r ]*$/i.test(value) ? Number(value) : NaN;
  return Number.isFinite(rank) ? rank : Number.isFinite(node.zIndex) ? node.zIndex! : 0;
}

/** The group's frontmost member owns its unit's position in the paint order. */
export function stackUnits(nodes: Node[]): Node[][] {
  const sorted = nodes.map((node, index) => ({ node, index })).sort((a, b) => effectiveStack(a.node) - effectiveStack(b.node) || a.index - b.index);
  const groups = new Map<string, Node[]>();
  for (const { node } of sorted) {
    const id = groupId(node);
    if (id !== undefined) groups.set(id, [...(groups.get(id) ?? []), node]);
  }
  return sorted.flatMap(({ node }) => {
    const id = groupId(node), members = id === undefined ? undefined : groups.get(id);
    return !members ? [[node]] : members[members.length - 1] === node ? [members] : [];
  });
}

export function rankedNodes(nodes: Node[], start = 0): Node[] {
  return nodes.map((node, index) => ({ ...node, zIndex: start + index,
    ...(node.style && Object.prototype.hasOwnProperty.call(node.style, "zIndex") ? { style: { ...node.style, zIndex: start + index } } : {}) }));
}

/** Shape validation stays in Zod. These checks govern references and flat topology. */
export function normalizeGroups<T extends CanvasGraph>(graph: T): T {
  const organized = hasGroups(graph);
  if (organized) {
    const identities = new Set<string>();
    for (const item of [...graph.nodes, ...graph.edges]) {
      if (identities.has(item.id)) throw new Error("Duplicate organization identity");
      identities.add(item.id);
    }
    const ids = new Set(graph.nodes.map(node => node.id));
    if (graph.nodes.some(node => hasParentTopology(node) || (groupId(node) !== undefined && identities.has(groupId(node)!))) ||
      graph.edges.some(edge => !ids.has(edge.source) || !ids.has(edge.target)))
      throw new Error("Invalid flat organization references");
  }
  const counts = new Map<string, number>();
  for (const node of graph.nodes) {
    const id = groupId(node);
    if (id !== undefined) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  let nodes = graph.nodes.map(node => {
    const id = groupId(node);
    if ((node as OrganizedNode).group_id === null || (id !== undefined && counts.get(id) === 1)) {
      const clean: OrganizedNode = { ...node }; delete clean.group_id; return clean;
    }
    return node;
  });
  if (organized) nodes = rankedNodes(stackUnits(nodes).flat());
  return nodes.every((node, index) => node === graph.nodes[index]) ? graph : { ...graph, nodes };
}

export function expandGroupSelection(nodes: Node[], ids: Iterable<string>): Set<string> {
  const selected = new Set(ids), groups = new Set(nodes.filter(node => selected.has(node.id)).map(groupId).filter(id => id !== undefined));
  for (const node of nodes) { const id = groupId(node); if (id !== undefined && groups.has(id)) selected.add(node.id); }
  return selected;
}

/** Click intent toggles a group; marquee diffs close the resulting inside-set. */
export function groupSelectionChanges(nodes: Node[], changes: { id: string; selected: boolean }[], marquee = false): Node[] {
  const intent = new Map(changes.map(change => [change.id, change.selected]));
  const groupIntent = new Map<string, boolean>();
  for (const change of changes) {
    const node = nodes.find(node => node.id === change.id), id = node && groupId(node);
    if (id !== undefined) groupIntent.set(id, (groupIntent.get(id) ?? false) || change.selected);
  }
  const selected = expandGroupSelection(nodes, nodes.filter(node => node.selected).map(node => node.id));
  return nodes.map(node => {
    const id = groupId(node);
    const nextSelected = marquee ? selected.has(node.id) : id !== undefined && groupIntent.has(id) ? groupIntent.get(id)!
      : intent.get(node.id) ?? selected.has(node.id);
    return nextSelected === !!node.selected ? node : { ...node, selected: nextSelected };
  });
}

export function allocateGroupId(graph: CanvasGraph, factory = () => `group_${crypto.randomUUID()}`): string | null {
  const ids = new Set([...graph.nodes, ...graph.edges].map(item => item.id));
  for (const node of graph.nodes) { const id = groupId(node); if (id !== undefined) ids.add(id); }
  for (let attempt = 0; attempt < 100; attempt++) { const id = factory(); if (id && !ids.has(id)) return id; }
  return null;
}
